import React, { useContext, createContext, ReactNode } from 'react';
import { User } from '../sidebar/models/User';
import { Comment } from '../sidebar/models/Comment';
import { UpdateComment } from '../shared/updateComment';
import { Assignee } from '../sidebar/views/SidebarPanel/Detailbar/CommentsAndLog';
import { MentionableTask } from '../sidebar/views/CreateTask/create_task/types';

type CommentsState = {
  currentUserId: string | number;
  users: User[];
  usersNotOnProject: User[];
  getContainer: () => HTMLDivElement;
  isAdminView?: boolean;
  createComment: (commentBody: any) => void;
  deleteComment: (commentId: number) => void;
  updateComment: UpdateComment;
  canViewPrivateComments: boolean;
  canCreatePrivateComments: boolean;
  billingRights: boolean;
  owner: User;
  bugherdUrl: string;
  organizationId: string | number;
  guestVisible: boolean;
  taskId: number;
  isSmallView?: boolean;
  isNewSidebar?: boolean;
  commentsLoading: boolean;
  canEditComment: boolean;
  comments: Comment[];
  children?: ReactNode;
  onPlanUpgradeClick?: () => void;
  projectId: number;
  canViewCommitComment: boolean;
  apiDomain: string;
  assignGuestsAllowed: boolean;
  assignGuests: boolean;
  assignees: Assignee[];
  mentionableTasks: MentionableTask[];
  searchMentionableTasks?: (query: string, callback: (results: unknown[]) => void) => void;
  setActiveTask: (taskId: number) => void;
  page: number;
  setPage: (page: number) => void;
};

// @ts-ignore
const CommentsStateContext = createContext<CommentsState>();

const CommentsProvider = ({
  currentUserId,
  users,
  usersNotOnProject,
  getContainer,
  isAdminView,
  createComment,
  deleteComment,
  updateComment,
  canViewPrivateComments,
  canCreatePrivateComments,
  canViewCommitComment,
  billingRights,
  owner,
  bugherdUrl,
  organizationId,
  guestVisible,
  taskId,
  isSmallView,
  isNewSidebar,
  commentsLoading,
  canEditComment,
  comments,
  children,
  onPlanUpgradeClick,
  projectId,
  apiDomain,
  assignGuestsAllowed,
  assignGuests,
  assignees,
  mentionableTasks,
  searchMentionableTasks,
  setActiveTask,
  page,
  setPage
}: CommentsState) => {
  return (
    <CommentsStateContext.Provider
      value={{
        currentUserId,
        users,
        usersNotOnProject,
        getContainer,
        isAdminView,
        createComment,
        deleteComment,
        updateComment,
        canViewPrivateComments,
        canCreatePrivateComments,
        canViewCommitComment,
        billingRights,
        owner,
        bugherdUrl,
        organizationId,
        guestVisible,
        taskId,
        isSmallView,
        isNewSidebar,
        commentsLoading,
        canEditComment,
        comments,
        onPlanUpgradeClick,
        projectId,
        apiDomain,
        assignGuestsAllowed,
        assignGuests,
        assignees,
        mentionableTasks,
        searchMentionableTasks,
        setActiveTask,
        page,
        setPage
      }}
    >
      {children}
    </CommentsStateContext.Provider>
  );
};

const useCommentsState = () => {
  const context = useContext(CommentsStateContext);

  if (context === undefined) {
    throw new Error('useCommentsState must be used within a CommentsProvider');
  }

  return context;
};

export { CommentsProvider, useCommentsState, CommentsState };
